import { Inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { forkJoin, Observable, of } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { MeetingSchedulerStoreService } from './data-providers/meeting-scheduler-store.service';
import { MeetingSchedulerServiceModule } from './data-providers/meeting-scheduler-service.module';
import { CALENDAR_VIEW_PAGE_LINK_TOKEN, SETTINGS_LINK_TOKEN } from './data-providers/providers';

@Injectable({
  providedIn: MeetingSchedulerServiceModule,
})
export class ScheduledMeetingsGuard {
  constructor(
    @Inject(SETTINGS_LINK_TOKEN) private readonly settingsLink$: Observable<string>,
    @Inject(CALENDAR_VIEW_PAGE_LINK_TOKEN) private readonly calendarLink$: Observable<string>,
    private readonly meetingSchedulerStoreService: MeetingSchedulerStoreService,
    private readonly router: Router,
  ) {}

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> {
    // Check if this is a direct action route (cancel/reschedule from email)
    const hasDirectAction = route.data && (route.data['action'] === 'cancel' || route.data['action'] === 'reschedule');
    const eventId = route.params['eventId'];
    
    if (hasDirectAction && eventId) {
      // For direct actions from emails, always allow through regardless of configuration
      // The component will handle showing appropriate errors if not configured
      return of(true);
    }

    return this.meetingSchedulerStoreService.loadHasHostEverBeenConfigured().pipe(
      take(1),
      switchMap((isConfigured) => {
        const currentStep = this.meetingSchedulerStoreService.wizardStep;
        const currentUrl = state.url.split('?')[0];
        const currentStepInUrl = +route.queryParams['step'];

        return forkJoin({
          settingsLink: this.settingsLink$.pipe(take(1)),
          calendarLink: this.calendarLink$.pipe(take(1)),
        }).pipe(
          map(({ settingsLink, calendarLink }) => {
            const isOnSettingsPage = currentUrl.startsWith(settingsLink);

            if (isConfigured) {
              if (isOnSettingsPage) {
                // Redirect away from settings if already configured
                return this.router.createUrlTree([calendarLink], {
                  queryParams: {},
                });
              }
              return true;
            }
            // To Avoid infinite loop
            if (isOnSettingsPage) {
              const isLatestStep = currentStepInUrl === currentStep;
              if (!isLatestStep) {
                return this.router.createUrlTree([settingsLink], {
                  queryParams: { step: currentStep },
                  queryParamsHandling: '',
                });
              }
              return true;
            }
            // Not configured, redirect to settings page with step param
            return this.router.createUrlTree([settingsLink], {
              queryParams: { currentStep },
              queryParamsHandling: '',
            });
          }),
        );
      }),
    );
  }
}
